import { Injectable, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';

import { RestapiService } from '@app/services/restapi.service';

import { TimezoneService } from '../../system-configuration/components/date-time-configuration/timezone.service';
import { TimezoneEntityHelper } from '../../system-configuration/components/timezone-entity.helper';

export interface CustomFieldModel {
  id?: number;
  name?: string;
  roles?: CustomFieldAccessModel[];
  customFieldType?: CustomFieldType;
  appliesTo?: CustomFieldDestination[]; // FE field
  appliesToMask?: number;
  defaultValueInt?: number;
  defaultValueText?: string;
  defaultValueNumber?: number;
  defaultValueDate?: Date;
  defaultValueTime?: string; // FE field
  defaultValueDateTime?: Date;
  defaultValueUserId?: number;
  defaultValueMoney?: number;
  defaultValueDropDown?: string;
  defaultValueCheckList?: string;
  defaultValueRadioList?: string;
  defaultValueBool?: boolean;
  dropDownValues?: string; // JSON.stringify
  addToSearchAndFilters?: boolean;
  isRequired?: boolean;
  isDeleted?: boolean;
  createdBy?: number;
  createdAt?: Date;
  modifiedBy?: number;
  modifiedAt?: Date;
  deletedBy?: number;
  deletedAt?: Date;
  canView?: boolean; // FE field
  canEdit?: boolean; // FE field
}

export interface PropertyBaseCustomFieldsDataModel {
  id?: number;
  createdAt?: string;
  createdBy?: number;
  modifiedAt?: string;
  modifiedBy?: number;
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: number;
  defaultValueProvisionedOn?: string;
  propertyBaseId: number;
  propertyRefId?: number;
  customFieldId: number;
  intValue?: number;
  numberValue?: number;
  dateValue?: string;
  dateTimeValue?: string;
  userIdValue?: number;
  moneyValue?: number;
  dropDownValue?: string;
  boolValue?: boolean;
  textValue?: string;
}

export interface PropertyCustomFieldStateModel {
  id?: number;
  effectiveDate?: string;
  modifiedBy?: number;
  isDeleted?: boolean;
  propertyBaseId?: number;
  propertyRefId?: number;
  customFieldId?: number;
  intValue?: number;
  numberValue?: number;
  dateValue?: string;
  dateTimeValue?: string;
  userIdValue?: number;
  moneyValue?: number;
  dropDownValue?: string;
  boolValue?: boolean;
  textValue?: string;
}

export type CustomFieldContext = 'portfolio' | 'property' | 'unit';

export enum CustomFieldDestination {
  Unit = 1,
  Property = 2,
  Portfolio = 4,
}

export enum CustomFieldType {
  Integer = 1,
  Decimal,
  Date,
  DateTime,
  User,
  Amount,
  DropDown,
  Boolean,
  Text,
  CheckList, // FE only for now
  RadioList, // FE only for now
}

export interface UpdateCustomFieldAccessModel {
  customFieldId: number;
  roles?: CustomFieldAccessModel[];
}

export interface CustomFieldAccessModel {
  roleId: number;
  canEdit: boolean;
}

export interface CustomDropDownOption {
  label: string;
  value?: string;
  color?: string;
  group?: string;
}

export type DropDownOptionFormGroup = FormGroup<{
  value: FormControl<string>;
  label: FormControl<string>;
  color: FormControl<string>;
  group?: FormControl<string>;
}>;

@Injectable({
  providedIn: 'root',
})
export class CustomFieldsService {
  private restApiService: RestapiService = inject(RestapiService);
  private timezoneService: TimezoneService = inject(TimezoneService);

  private readonly baseUrl = 'customfields';

  getAll(propertyId?: number): Observable<CustomFieldModel[]> {
    let url = `${this.baseUrl}/list`;
    if (propertyId) url += `?propertyId=${propertyId}`;
    return this.restApiService.getData<CustomFieldModel[]>(url, TimezoneEntityHelper.fixTimezoneForCustomFieldsList);
  }

  getById(id: number): Observable<CustomFieldModel> {
    return this.restApiService.getData<CustomFieldModel>(
      `${this.baseUrl}/${id}`,
      TimezoneEntityHelper.fixTimezoneForCustomFieldsModelToClient
    );
  }

  create(payload: CustomFieldModel): Observable<CustomFieldModel> {
    return this.restApiService.post<CustomFieldModel>(
      this.baseUrl,
      payload,
      TimezoneEntityHelper.fixTimezoneForCustomFieldsModelToServer
    );
  }

  update(id: number, payload: CustomFieldModel): Observable<CustomFieldModel> {
    return this.restApiService.update<CustomFieldModel>(
      `${this.baseUrl}/${id}`,
      payload,
      TimezoneEntityHelper.fixTimezoneForCustomFieldsModelToServer
    );
  }

  delete(id: number): Observable<void> {
    return this.restApiService.delete(`${this.baseUrl}/${id}`);
  }

  changeAccess(payload: UpdateCustomFieldAccessModel): Observable<void> {
    return this.restApiService.post<void>(`${this.baseUrl}/changeAccess`, payload);
  }

  getCustomFieldByContextId(
    context: CustomFieldContext,
    contextId: number
  ): Observable<PropertyBaseCustomFieldsDataModel[]> {
    return this.restApiService.getData<PropertyBaseCustomFieldsDataModel[]>(
      `${this.baseUrl}/${context}/${contextId}`,
      TimezoneEntityHelper.fixTimezoneForCustomFieldPropertyContextListModelToClient
    );
  }

  updateCustomFieldById(id: number, payload: PropertyBaseCustomFieldsDataModel): Observable<boolean> {
    return this.restApiService.update<boolean>(
      `${this.baseUrl}/${id}`,
      payload,
      TimezoneEntityHelper.fixTimezoneForCustomFieldPropertyContextModelToServer
    );
  }

  deleteCustomFieldById(id: number): Observable<void> {
    return this.restApiService.delete(`${this.baseUrl}/${id}`);
  }

  createBaseCustomFields(
    context: CustomFieldContext,
    payload: PropertyBaseCustomFieldsDataModel
  ): Observable<PropertyBaseCustomFieldsDataModel> {
    return this.restApiService.post<PropertyBaseCustomFieldsDataModel>(
      `${this.baseUrl}/${context}`,
      payload,
      TimezoneEntityHelper.fixTimezoneForCustomFieldPropertyContextModelToServer
    );
  }

  updateBaseCustomFields(
    context: CustomFieldContext,
    payload: PropertyBaseCustomFieldsDataModel
  ): Observable<PropertyBaseCustomFieldsDataModel> {
    return this.restApiService.update<PropertyBaseCustomFieldsDataModel>(
      `${this.baseUrl}/${context}`,
      payload,
      TimezoneEntityHelper.fixTimezoneForCustomFieldPropertyContextModelToServer
    );
  }

  getBaseCustomField(
    context: CustomFieldContext,
    propertyBaseId: number,
    customFieldId: number
  ): Observable<PropertyBaseCustomFieldsDataModel> {
    return this.restApiService.getData<PropertyBaseCustomFieldsDataModel>(
      `${this.baseUrl}/${context}/${propertyBaseId}/customField/${customFieldId}`,
      TimezoneEntityHelper.fixTimezoneForCustomFieldPropertyContextModelToClient
    );
  }

  createBaseCustomFieldByContextId(
    context: CustomFieldContext,
    payload: PropertyBaseCustomFieldsDataModel
  ): Observable<PropertyBaseCustomFieldsDataModel> {
    return this.restApiService.create<PropertyBaseCustomFieldsDataModel>(
      `${this.baseUrl}/${context}`,
      payload,
      TimezoneEntityHelper.fixTimezoneForCustomFieldPropertyContextModelToServer
    );
  }

  updateBaseCustomFieldByContextId(
    context: CustomFieldContext,
    payload: PropertyBaseCustomFieldsDataModel
  ): Observable<PropertyBaseCustomFieldsDataModel> {
    return this.restApiService.update<PropertyBaseCustomFieldsDataModel>(
      `${this.baseUrl}/${context}`,
      payload,
      TimezoneEntityHelper.fixTimezoneForCustomFieldPropertyContextModelToServer
    );
  }

  deleteBaseCustomFieldByContextId(
    context: CustomFieldContext,
    propertyBaseId: number,
    customFieldId: number
  ): Observable<void> {
    return this.restApiService.delete(`${this.baseUrl}/${context}/${propertyBaseId}/customField/${customFieldId}`);
  }

  getCustomFieldHistory(
    propertyBaseId: number,
    customFieldId: number,
    limit = 50,
    offsetId?: number
  ): Observable<PropertyCustomFieldStateModel[]> {
    let url = `${this.baseUrl}/${propertyBaseId}/${customFieldId}/history?limit=${limit}`;
    if (offsetId != null) {
      url += `&offsetId=${offsetId}`;
    }
    return this.restApiService.getData<PropertyCustomFieldStateModel[]>(
      url,
      TimezoneEntityHelper.fixTimezoneForCustomFieldContextListModelToClient
    );
  }

  formatAmount(value: number): string {
    if (!value || value === null) return null;
    const parts = value.toString().split('.');
    const wholePart = parts[0];
    const fractionalPart = parts?.length > 1 ? '.' + parts[1] : '';
    return '$' + wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + fractionalPart;
  }

  parseAmount(value: string): number {
    if (!value || value === '0') return null;
    return parseFloat(value.replace(/[^0-9.-]+/g, ''));
  }

  getDateTimes(date?: Date): { value: Date; label: string }[] {
    const today = date || this.timezoneService.getCurrentDateOnly();
    const appointments: { value: Date; label: string }[] = [];

    appointments.push({
      value: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0),
      label: '12:00 AM',
    });

    for (let hour = 1; hour <= 11; hour++) {
      const label = `${hour}:00 AM`;
      const appointmentDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hour, 0, 0);
      appointments.push({ value: appointmentDate, label });
    }

    appointments.push({
      value: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0),
      label: '12:00 PM',
    });

    for (let hour = 1; hour <= 11; hour++) {
      const label = `${hour}:00 PM`;
      const appointmentDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hour + 12, 0, 0);
      appointments.push({ value: appointmentDate, label });
    }

    return appointments;
  }
}
